<template>
	<article class="activity-reaction-detail">
		<ActivityDetailHeader
			v-if="showHeader"
			:notification-topic="activity.notificationTopic"
			:title="activity.title"
			:timestamp="activity.timestamp"
		/>

		<ActivityRelatedTarget
			:target="target"
			:target-id="activity.opportunityIds[0] ?? null"
			:project-id="activity.projectId"
			:loading="loading"
			:load-error="loadError"
		/>

		<ActivityLinkType
			v-if="activity.projectId"
			:link-type="LinkType.PROJECT"
			:title="activity.project || t('activityPage.details.unknownProject')"
			:summary="projectSummary"
			@open="viewProject"
		/>
	</article>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import ActivityDetailHeader from "@/components/activity/ActivityDetailHeader.vue"
import ActivityLinkType from "@/components/activity/ActivityLinkType.vue"
import ActivityRelatedTarget from "@/components/activity/ActivityRelatedTarget.vue"
import { LinkType, type ActivityDetail } from "@/types/activity"

const props = withDefaults(
	defineProps<{
		activityDetail: ActivityDetail
		loading?: boolean
		loadError?: boolean
		showHeader?: boolean
	}>(),
	{
		loading: false,
		loadError: false,
		showHeader: true,
	}
)

const emit = defineEmits<{
	"view-project": [projectId: string]
}>()

const { t } = useI18n()

const activity = computed(() => {
	const activity = props.activityDetail.activity

	if (activity.linkType !== LinkType.PROJECT) {
		throw new Error("Reaction activity must be linked to a project")
	}

	return activity
})

const target = computed(() => props.activityDetail.opportunities[0] ?? null)

const projectSummary = computed(() => {
	if (activity.value.targetsCount === null) {
		return t("nav.hunt")
	}

	return t("activityPage.details.projectSummary", {
		hunt: t("nav.hunt"),
		count: activity.value.targetsCount,
	})
})

function viewProject(): void {
	if (!activity.value.projectId) {
		return
	}

	emit("view-project", activity.value.projectId)
}
</script>

<style scoped>
.activity-reaction-detail {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xl);
	width: min(100%, 560px);
	font-family: var(--font-family-primary);
}
</style>
